import { dist, clamp, lerp, damp } from '@utils/math/maths.js'

const LETTER_CLASS = 'js-letter'

// Rest weights mirror page.scss, which keeps them for the first paint before JS runs.
const VARIANTS = [
  { selector: '.js-animateText', rest: 700, near: 100 },
  { selector: '.js-animateText--invert', rest: 200, near: 700 },
]

const RADIUS = 100 // px, distance where the weight is back at rest
const SMOOTHING = 0.15 // damp factor, close to a 0.4s power2.out
const SETTLE_THRESHOLD = 0.5 // wght units

/**
 * Splits the matched elements into per-letter spans. Each letter weight is
 * driven by its distance to the pointer, with a linear falloff over RADIUS,
 * then damped every frame.
 *
 * The pointer is polled from the shared TouchController in `update()`.
 */
export default class TextWeightHover {
  constructor({ touch, radius = RADIUS } = {}) {
    this.touch = touch
    this.radius = radius
    this.hasPointer = false

    this.bindMethods()
    this.mount()
  }

  bindMethods() {
    this.measure = this.measure.bind(this)
    this.onFirstMove = this.onFirstMove.bind(this)
  }

  mount() {
    this.blocks = []

    for (const variant of VARIANTS) {
      for (const el of document.querySelectorAll(variant.selector)) {
        el.innerHTML = this.splitText(el.textContent)
        this.blocks.push({ el, variant, letters: this.createLetters(el, variant), settled: true })
      }
    }

    this.measure()

    // Fonts loading after the initial measure would otherwise leave stale
    // (wrong) locked widths once the real webfont swaps in.
    document.fonts?.ready?.then(this.measure)
    window.addEventListener('resize', this.measure)

    // TouchController starts at (0, 0): skip until the pointer really moves,
    // otherwise the top-left title lights up on load.
    window.addEventListener('pointermove', this.onFirstMove, { once: true })
  }

  splitText(text) {
    return text.split('')
      .map((char) => {
        const c = char === ' ' ? '&nbsp;' : char
        return `<span class="${LETTER_CLASS}">${c}</span>`
      })
      .join('')
  }

  createLetters(el, variant) {
    return Array.from(el.children, (letterEl) => {
      letterEl.style.fontVariationSettings = `'wght' ${variant.rest}`
      return { el: letterEl, x: 0, y: 0, weight: variant.rest, written: variant.rest }
    })
  }

  onFirstMove() {
    this.hasPointer = true
  }

  // Widths are locked so a weight change never reflows the line. The letter
  // centres are stored relative to their block, and stay valid while the widths do.
  measure() {
    for (const block of this.blocks) {
      for (const letter of block.letters) letter.el.style.width = 'auto'
    }

    for (const block of this.blocks) {
      const blockRect = block.el.getBoundingClientRect()

      for (const letter of block.letters) {
        const rect = letter.el.getBoundingClientRect()
        letter.el.style.width = `${rect.width}px`
        letter.x = rect.left - blockRect.left + rect.width * 0.5
        letter.y = rect.top - blockRect.top + rect.height * 0.5
      }
    }
  }

  // Polled once per frame from HomePage, dt in ms. Block rects are read first,
  // before any style write, so the frame never forces a second layout.
  update(dt) {
    if (!this.touch || !this.hasPointer) return

    const points = this.activePointers()

    for (const block of this.blocks) {
      const rect = block.el.getBoundingClientRect()
      const inRange = points.some((point) => this.isNearRect(rect, point))

      if (!inRange && block.settled) continue

      block.settled = this.updateBlock(block, rect, inRange ? points : null, dt)
    }
  }

  updateBlock(block, rect, points, dt) {
    const { rest, near } = block.variant
    let settled = true

    for (const letter of block.letters) {
      const influence = points ? this.influence(rect.left + letter.x, rect.top + letter.y, points) : 0
      const target = lerp(rest, near, influence)

      letter.weight = damp(letter.weight, target, SMOOTHING, dt)
      if (Math.abs(letter.weight - target) < SETTLE_THRESHOLD) letter.weight = target
      else settled = false

      const rounded = Math.round(letter.weight)
      if (rounded === letter.written) continue

      letter.written = rounded
      letter.el.style.fontVariationSettings = `'wght' ${rounded}`
    }

    return settled
  }

  // Strongest pointer wins, so two fingers on two words both light up.
  influence(x, y, points) {
    let value = 0
    for (const point of points) {
      value = Math.max(value, 1 - dist(x, y, point.x, point.y) / this.radius)
    }
    return clamp(value, 0, 1)
  }

  isNearRect(rect, point) {
    const r = this.radius
    return point.x > rect.left - r && point.x < rect.right + r
      && point.y > rect.top - r && point.y < rect.bottom + r
  }

  // TouchController averages multiple fingers into a single `pos` for
  // pinch/rotate gestures, so read each finger's own position instead.
  activePointers() {
    const points = []
    for (const finger of this.touch.state.fingers) {
      if (finger.used) points.push(finger.position)
    }
    if (points.length === 0) points.push(this.touch.state.pos)
    return points
  }

  destroy() {
    window.removeEventListener('resize', this.measure)
    window.removeEventListener('pointermove', this.onFirstMove)

    for (const block of this.blocks) {
      for (const letter of block.letters) {
        letter.el.style.fontVariationSettings = `'wght' ${block.variant.rest}`
      }
    }
  }
}
